import { MemreEditableHeading, MemreModal } from 'components/core';
import SecondaryButton from 'components/buttons/v4/SecondaryButton';
import ImageBadge from 'components/images/ImageBadge';
import ImageUploadModal from './ImageUpload/ImageUploadModal';
import useDebounce from 'hooks/useDebounce';
import { useAuthoringContext } from 'pages/authoring/AuthoringContext';
import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

export default function AuthoringHeading() {
  const { set, renameSet, updateSetImage } = useAuthoringContext();

  const [setName, setSetName] = useState(set?.name);
  const [modalContent, setModalContent] = useState<JSX.Element | null>(null);

  // using a ref to store the previous name to avoid spamming the server
  // as the component is re-rendered excessively due to an issue elsewhere in the codebase
  const previousNameRef = useRef(set?.name);
  const isInitialized = useRef(false);
  const debouncedSetName = useDebounce(setName, 500);

  useEffect(() => {
    if (set?.name !== previousNameRef.current) {
      setSetName(set?.name);
      previousNameRef.current = set?.name;

      // Delay initialization to avoid renaming on first load (3 seconds)
      // I couldn't find a better way than just using a 3 second delay.
      setTimeout(() => {
        isInitialized.current = true;
      }, 3000);
    }
  }, [set?.name]);

  useEffect(() => {
    if (!isInitialized.current) return;

    if (debouncedSetName !== 'Untitled' && debouncedSetName !== previousNameRef.current) {
      renameSet(debouncedSetName);
      previousNameRef.current = debouncedSetName;
    }
  }, [debouncedSetName, renameSet]);

  const handleNameChange = useCallback((e) => {
    setSetName(e.target.value);
  }, []);

  const handleEditImage = useCallback(() => {
    setModalContent(
      <ImageUploadModal
        addImageMethod={(url: string, id: string) => {
          updateSetImage(id);
          setModalContent(null);
        }}
        close={() => setModalContent(null)}
      />
    );
  }, [updateSetImage]);

  return (
    <>
      <HeadingContainer>
        <ImageBadge
          image={set?.image}
          entity="set"
          onEdit={handleEditImage}
          style={{ height: '48px', width: '48px' }}
        />
        <MemreEditableHeading text={setName} onChange={handleNameChange} />
      </HeadingContainer>
      <MemreModal>{modalContent}</MemreModal>
    </>
  );
}

const HeadingContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;
